/**
 * The lazy boundaries of the SPA and the message namespace each one loads (planning-app.md §4.5,
 * `v2.10`). `SCOPE_NAMESPACES` is the catalog chunk `<MessageScope ns="…">` puts around a boundary
 * (`scripts/i18n.ts` MEMBERSHIP says which ids a namespace carries); `SCOPE_ENTRIES` is the module
 * the dynamic import starts at, relative to `src/`, read only by `npm run i18n:check`, which walks
 * each entry's static imports and refuses an id that would be missing inside its boundary, and
 * checks that the entries agree with `./lazy.ts`. The rooms are the keys of `loaders` there; the
 * last three are pieces of the shell that only load when used. Two separate constants so the
 * browser bundle keeps the namespaces and drops the paths. No imports: the script loads this
 * module outside Vite.
 */
export const SCOPE_NAMESPACES = {
  pulse: 'pulse',
  /** A decision's page and a session's page read under Tracks (v3.8b) but keep their own catalogs: the track page's
   * Decisions tab loads them as nested boundaries (pages/track/DecideTab.tsx), so `/decisions/:id` stays under the §4.5 budget. */
  decision: 'decide',
  session: 'session',
  decideList: 'decide',
  sessionsList: 'session',
  status: 'status',
  workspace: 'workspace',
  /** The thread page and the chat shell it mounts share one catalog (v3.12, IA §5): `chat.*`, the agent run and amendment cards. */
  thread: 'chat',
  chat: 'chat',
  /** AI Assist's panel (v3.15a, PA64): the same chat shell and catalog, as a boundary the shell opens on every route, plus
   * its own `assist` catalog nested inside it (v3.18 lane 14): the home's words and the room-specific asks (`chat.assist.*`)
   * ship only with the panel, so the thread page — a §4.5 budget page — does not carry them. */
  assist: ['chat', 'assist'],
  you: 'you',
  /** Activity (§8 *Activity*, v3.54): the maintainers' page under You. */
  activity: 'activity',
  /** The gate page ships with Plan, the finding page with the Build console (v3.8c, IA §1.5); `verify.*` ids live in both catalogs. */
  gate: 'plan',
  finding: 'build',
  plan: 'plan',
  planChange: 'plan',
  package: 'plan',
  /** The calendar (IA §6.8, v3.10): the plan catalog, its own code chunk. */
  calendar: 'plan',
  /** The track list and the track page with its tabs (planning-app.md §3.3 *Track pages*, v2.13; `/tracks` since v3.8a). */
  units: 'track',
  unit: 'track',
  /** User Stories (IA §3, v3.9a): the list and the story page share one catalog; the track page's Stories tab mounts the list as a nested boundary (pages/track/StoriesTab.tsx). */
  stories: 'stories',
  story: 'stories',
  storiesList: 'stories',
  reportAuthorize: 'report',
  docs: 'docs',
  doc: 'docs',
  translationQueue: 'docs',
  wireframes: 'wireframes',
  wireframe: 'wireframes',
  wireframeStale: 'wireframes',
  build: 'build',
  buildRun: 'build',
  /** The sign-in page: only the signed-out visit needs it (App.tsx). */
  signIn: 'signIn',
  /** The hand-off sheet behind the top bar's button and on the plan page (components/LazyHandoffSheet.tsx). */
  handoff: 'handoff',
  /** The report widget's labels, fetched with the widget after sign-in (components/report/useReportWidget.ts). */
  reportWidget: 'widget',
} as const;

export type ScopeKey = keyof typeof SCOPE_NAMESPACES;

export const SCOPE_ENTRIES: Record<ScopeKey, string> = {
  pulse: 'pages/PulsePage.tsx',
  decision: 'pages/decisions/DecisionPage.tsx',
  session: 'pages/sessions/SessionPage.tsx',
  decideList: 'pages/track/DecisionList.tsx',
  sessionsList: 'pages/track/TrackSessions.tsx',
  status: 'pages/StatusPage.tsx',
  workspace: 'pages/workspace/WorkspacePage.tsx',
  thread: 'pages/workspace/ThreadPage.tsx',
  chat: 'pages/chat/ChatMount.tsx',
  assist: 'pages/chat/AssistPanel.tsx',
  you: 'pages/YouPage.tsx',
  activity: 'pages/activity/ActivityPage.tsx',
  gate: 'pages/verification/GatePage.tsx',
  finding: 'pages/verification/FindingPage.tsx',
  plan: 'pages/plan/PlanPage.tsx',
  planChange: 'pages/plan/PlanChangePage.tsx',
  package: 'pages/plan/PackagePage.tsx',
  calendar: 'pages/plan/CalendarPage.tsx',
  units: 'pages/track/UnitsPage.tsx',
  unit: 'pages/track/UnitPage.tsx',
  stories: 'pages/stories/StoriesPage.tsx',
  story: 'pages/stories/StoryPage.tsx',
  storiesList: 'pages/stories/StoriesPage.tsx',
  reportAuthorize: 'pages/ReportAuthorizePage.tsx',
  docs: 'pages/docs/DocsPage.tsx',
  doc: 'pages/docs/DocPage.tsx',
  translationQueue: 'pages/docs/TranslationQueuePage.tsx',
  wireframes: 'pages/wireframes/WireframesPage.tsx',
  wireframe: 'pages/wireframes/WireframePage.tsx',
  wireframeStale: 'pages/wireframes/StaleBatchPage.tsx',
  build: 'pages/build/BuildPage.tsx',
  buildRun: 'pages/build/BuildRunPage.tsx',
  signIn: 'pages/SignInPage.tsx',
  handoff: 'components/HandoffSheet.tsx',
  reportWidget: 'components/report/widgetLabels.ts',
};
